import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  PRICELIST_CASE_FILE_ROLES,
  PricelistReceivedAs,
} from "@erp/shared-types";
import {
  TaskAssigneeTypeSchema,
  TaskPrioritySchema,
  TaskStatusSchema,
  TaskTimelineEntrySchema,
} from "../tasks.contract";
import {
  CommentResponseSchema,
  ListCommentsResponseSchema,
} from "../comments.contract";
import {
  AnticipatedChangeTextSchema,
  DivisionPriceChangeSchema,
  PricelistDateSchema,
  PricelistEventSchema,
} from "./pricelist.contract";
import { listSortShape } from "../../schemas/common";
import {
  PricelistCaseListFiltersSchema,
  PricelistCaseSortBySchema,
} from "./pricelist-case-filters.contract";

/**
 * Pricelist Update Cases (GEN-2524).
 *
 * A case is one vendor price change: an `im_pricelist_cases` row holding the
 * domain (effective date, division percentages, notes) paired 1:1 with a
 * `core_tasks` row holding the work (owner, status, comments, timeline). The
 * case page is the front door; there is no task page for this type.
 *
 * Deliberately a NEW contract file rather than more of `pricelist.contract.ts`
 * (already 600+ lines and covering the FILE, which is a different unit of
 * work). The percent and date primitives are reused from there so the two
 * cannot drift.
 *
 * Design contract: docs/projectplans/pricelist-update-cases.md.
 */

/** What one file contributes to a case. */
export const PricelistCaseFileRoleSchema = z.enum(PRICELIST_CASE_FILE_ROLES);

/** What the vendor sent: an advance notice, the price file itself, or both. */
const CaseReceivedAsSchema = z.nativeEnum(PricelistReceivedAs);

/**
 * The companion task as the case surfaces project it: the owner, the state and
 * the readable number. Not the task row — the case is the front door, so its
 * payload is the case's, not the task's.
 *
 * `taskDeleted` exists because `core_tasks` is paranoid: the case read path
 * resolves the task with `paranoid: false` so a soft-deleted task still
 * renders its case instead of dropping it from the list.
 */
export const PricelistCaseTaskSchema = z.object({
  id: z.uuid(),
  /** `PC-1042`; null only for a task that predates namespace numbering. */
  displayId: z.string().nullable(),
  status: TaskStatusSchema,
  priority: TaskPrioritySchema,
  assigneeType: TaskAssigneeTypeSchema.nullable(),
  assigneeUserId: z.uuid().nullable(),
  /** Display name of the assignee — enough to render an avatar and a name. */
  assigneeName: z.string().nullable(),
  dueDate: z.coerce.date().nullable(),
  outcome: z.string().nullable(),
  taskDeleted: z.boolean(),
});

export const PricelistCaseVendorSchema = z.object({
  companyId: z.uuid(),
  companyName: z.string(),
});

/**
 * A case vendor as the case page shows it (GEN-3074): the company plus the
 * specific vendor#s this price change is for. Empty `vendorNumbers` means
 * every vendor# the company carries — how every case behaved before.
 */
export const PricelistCaseDetailVendorSchema = PricelistCaseVendorSchema.extend(
  {
    vendorNumbers: z.array(z.string()),
  },
);

/** One row of the case list. */
export const PricelistCaseListRowSchema = z.object({
  id: z.uuid(),
  /** The readable case number, e.g. `PC-1042`. */
  caseNumber: z.string().nullable(),
  companyId: z.uuid(),
  companyName: z.string(),
  vendors: z.array(PricelistCaseVendorSchema).min(1),
  /**
   * True when the vendor has a Ship & Debit module config — the same tag the
   * pricelist company list shows, so a case reads as S&D at a glance.
   */
  shipAndDebitEnabled: z.boolean(),
  effectiveDate: PricelistDateSchema.nullable(),
  receivedDate: PricelistDateSchema.nullable(),
  fileCount: z.number().int().nonnegative(),
  /**
   * The RAW waiting-on key, not a label. Label resolution and the IM
   * waiting-on option set are Wave 3 (the resolver has to be extracted out of
   * AR first), so the client renders the key until then.
   */
  waitingOnKey: z.string().nullable(),
  followUpAt: z.coerce.date().nullable(),
  filesReadyAt: PricelistDateSchema.nullable(),
  task: PricelistCaseTaskSchema,
  createdAt: z.coerce.date(),
});

/** The manually maintained domain fields, mirroring the legacy details card. */
export const PricelistCaseDetailsSchema = z.object({
  divisionPriceChanges: z.array(DivisionPriceChangeSchema),
  notes: z.string().nullable(),
  fromTariffChange: z.boolean(),
  anticipatedVendorChangePercent: AnticipatedChangeTextSchema.nullable(),
  effectiveDate: PricelistDateSchema.nullable(),
  erpUploadDate: PricelistDateSchema.nullable(),
  receivedAs: CaseReceivedAsSchema.nullable(),
  receivedDate: PricelistDateSchema.nullable(),
});

/** One file attached to a case. */
export const PricelistCaseFileSchema = z.object({
  id: z.uuid(),
  documentId: z.uuid(),
  /**
   * The document's file name. Carried on the attachment row's DTO because the
   * case page's file list is unreadable without it — a list of roles and dates
   * says nothing about which file is which.
   */
  name: z.string(),
  role: PricelistCaseFileRoleSchema,
  position: z.number().int().nonnegative(),
  addedByUserId: z.uuid().nullable(),
  addedAt: z.coerce.date(),
  notes: z.string().nullable(),
  /** When the file note was last written. Null when the file has no note. */
  notesUpdatedAt: z.coerce.date().nullable(),
  deprecated: z.boolean(),
});

export const ListPricelistCasesInputSchema =
  PricelistCaseListFiltersSchema.extend({
    page: z.number().int().positive(),
    pageSize: z.number().int().positive().max(200),
    /** Newest first when absent. */
    ...listSortShape(PricelistCaseSortBySchema),
  });

export const ListPricelistCasesResponseSchema = z.object({
  cases: z.array(PricelistCaseListRowSchema),
  totalCount: z.number().int().nonnegative(),
});

export const GetPricelistCaseInputSchema = z.object({
  caseId: z.uuid(),
});

/**
 * The price-file-log columns that belong to the CASE and nowhere else — they
 * have no legacy `im_pricelist_details` counterpart, which is why they sit
 * beside `details` rather than inside it (the details shape is shared with the
 * file page and the seeding preview).
 */
export const PricelistCaseLogFieldsSchema = z.object({
  /**
   * The "3 LETTER CODE / PG" override. Null means the export uses
   * `companyCode` — the vendor's own code — which is the default for every
   * ordinary vendor; only a commodity file spanning many vendor numbers under
   * one code sets this (GEN-2578).
   */
  vendorCode: z.string().trim().max(32).nullable(),
  nationalImComparingComments: z.string().max(5000).nullable(),
  uploadComments: z.string().max(5000).nullable(),
});

export const PricelistCaseResponseSchema = PricelistCaseListRowSchema.extend({
  vendors: z.array(PricelistCaseDetailVendorSchema).min(1),
  details: PricelistCaseDetailsSchema,
  logFields: PricelistCaseLogFieldsSchema,
  /**
   * The vendor's own company code (its `originId` when that is a code rather
   * than a vendor number) — what the log exports when `vendorCode` is null.
   */
  companyCode: z.string().nullable(),
  /** The reminder workflow's cooldown gate; not the owner's follow-up date. */
  remindAfter: z.coerce.date().nullable(),
  notifyEmails: z.array(z.string().email()),
  filesReadyRemindAfter: z.coerce.date().nullable(),
  updatedAt: z.coerce.date(),
  /** Empty until Wave 2 attaches files from the upload list. */
  files: z.array(PricelistCaseFileSchema),
});

export const CreatePricelistCaseInputSchema = z.object({
  /** The vendor. Required — one vendor per case. */
  companyId: z.uuid(),
  /** Companion task title. Defaults to "<vendor> price change". */
  title: z.string().min(1).max(500).optional(),
  priority: TaskPrioritySchema.optional(),
  /**
   * Domain values recorded on the case. All optional: a case can be opened
   * knowing only the vendor. Wave 2's "create from selected files" seeds these
   * from the files' legacy details rows and shows them for correction, so it
   * layers on this input rather than replacing it.
   */
  details: PricelistCaseDetailsSchema.partial().optional(),
});

// ---------------------------------------------------------------------------
// Wave 2 — assembling a case from selected files
// ---------------------------------------------------------------------------

/**
 * How many files one selection action may carry. Mirrors
 * `MAX_CASE_FILES_PER_ACTION` in the assembly service, which enforces it again
 * for service callers.
 */
export const MAX_PRICELIST_CASE_FILES_PER_ACTION = 50;

const CaseDocumentIdsSchema = z
  .array(z.uuid())
  .min(1)
  .max(MAX_PRICELIST_CASE_FILES_PER_ACTION);

/** Which domain field a seeded value was copied into. */
export const PricelistCaseSeedFieldSchema = z.enum([
  "divisionPriceChanges",
  "notes",
  "fromTariffChange",
  "anticipatedVendorChangePercent",
  "effectiveDate",
  "erpUploadDate",
  "receivedAs",
  "receivedDate",
]);

/** One selected pricelist, as the create dialog needs to reason about it. */
export const PricelistCaseSelectionFileSchema = z.object({
  documentId: z.uuid(),
  name: z.string(),
  /** Upload time — the oldest selected file leads the case. */
  uploadedAt: z.coerce.date(),
  mimeType: z.string().nullable(),
  /** The file's OWN vendor assignment; null means it inherits the case's. */
  companyId: z.uuid().nullable(),
  companyName: z.string().nullable(),
  /** Defaulted from the MIME type; the dialog may change it. */
  defaultRole: PricelistCaseFileRoleSchema,
  /** 0 for the oldest selected file — the case's primary. */
  defaultPosition: z.number().int().nonnegative(),
  /** The case that already owns this file, which blocks a create. */
  existingCase: z
    .object({ caseId: z.uuid(), caseNumber: z.string().nullable() })
    .nullable(),
});

/** One vendor the selection could be a case for. */
export const PricelistCaseSelectionVendorSchema = z.object({
  companyId: z.uuid(),
  companyName: z.string(),
  fileCount: z.number().int().positive(),
});

/** Where one seeded value came from — never silent, always attributed. */
export const PricelistCaseSeedSourceSchema = z.object({
  field: PricelistCaseSeedFieldSchema,
  sourceDocumentId: z.uuid(),
  sourceDocumentName: z.string(),
  /** When that file's legacy details row was last saved. */
  sourceUpdatedAt: z.coerce.date(),
});

export const PreviewPricelistCaseSelectionInputSchema = z.object({
  documentIds: CaseDocumentIdsSchema,
});

/**
 * Everything the create dialog needs in one read: the per-file roles and
 * order, the vendors in play (so it can ASK rather than guess when the
 * selection spans several), the seeded domain values with their source files,
 * and the files a create is blocked on.
 *
 * The "which of these are already on a case" answer is folded in here rather
 * than given its own procedure: the dialog needs it at the same instant as
 * everything else, and a second round trip could disagree with this one.
 */
export const PreviewPricelistCaseSelectionResponseSchema = z.object({
  files: z.array(PricelistCaseSelectionFileSchema),
  vendors: z.array(PricelistCaseSelectionVendorSchema),
  /** Selected files with no vendor of their own. */
  unassignedFileCount: z.number().int().nonnegative(),
  /** The complete starting point, in the shape `createFromSelection` takes. */
  seededDetails: PricelistCaseDetailsSchema,
  seedSources: z.array(PricelistCaseSeedSourceSchema),
  blockedDocumentIds: z.array(z.uuid()),
});

/** One file as the dialog finally settled it. */
export const PricelistCaseFileChoiceSchema = z.object({
  documentId: z.uuid(),
  role: PricelistCaseFileRoleSchema,
});

/**
 * Array order IS the display order: the first entry becomes position 0, the
 * case's primary file. Positions are renumbered server-side from this order
 * rather than sent, so a client cannot leave a case without a primary.
 */
const CaseFileChoicesSchema = z
  .array(PricelistCaseFileChoiceSchema)
  .min(1)
  .max(MAX_PRICELIST_CASE_FILES_PER_ACTION);

export const CreatePricelistCaseFromSelectionInputSchema = z.object({
  /**
   * The vendor the dialog chose. Required even when the selection is
   * unanimous: one vendor per case, and a server that inferred it would be
   * guessing on the one field the dialog exists to settle.
   */
  companyId: z.uuid(),
  /**
   * GEN-3074: every vendor the dialog chose, primary first. Absent means just
   * `companyId`. Same limits as `update`.
   */
  companyIds: z.array(z.uuid()).min(1).max(20).optional(),
  /** Explicit vendor#s, validated as `update` validates them. */
  vendorNumbers: z.array(z.string().trim().min(1).max(32)).max(100).optional(),
  files: CaseFileChoicesSchema,
  /**
   * The dialog's FINAL domain values — seeded by `previewFromSelection`, shown
   * with their source file, then corrected. The server records what it is
   * sent and never re-seeds.
   */
  details: PricelistCaseDetailsSchema.partial().optional(),
  title: z.string().min(1).max(500).optional(),
  priority: TaskPrioritySchema.optional(),
});

export const AddFilesToPricelistCaseInputSchema = z.object({
  caseId: z.uuid(),
  files: CaseFileChoicesSchema,
});

export const DetachPricelistCaseFileInputSchema = z.object({
  caseId: z.uuid(),
  documentId: z.uuid(),
});

export const MAX_PRICELIST_CASE_FILE_NOTES = 2000;

export const UpdatePricelistCaseFileInputSchema = z.object({
  caseId: z.uuid(),
  documentId: z.uuid(),
  notes: z.string().max(MAX_PRICELIST_CASE_FILE_NOTES).nullable().optional(),
  deprecated: z.boolean().optional(),
});

/**
 * Hand a case to someone, or take it off them with `null`.
 *
 * The write lands on the COMPANION TASK (`core_tasks.assignee_user_id`), never
 * on the case: Rule 2 — no field exists on both rows, so the task owns the
 * work and therefore the owner. Authorized as the case all the same, and the
 * assignee gets the same `ASSIGNED` watch as any other task assignee.
 *
 * Users only. A case is handed to a person; agent assignment stays on the
 * generic task surfaces until a case type has an agent to run it.
 */
export const AssignPricelistCaseInputSchema = z.object({
  caseId: z.uuid(),
  /** An org user, or null to leave the case unassigned. */
  assigneeUserId: z.uuid().nullable(),
});

/**
 * Park a case: why it is waiting (`waitingOnKey`, a frozen key from the org's
 * `inventoryManagement.pricelistCases.waitingOnOptions`) and when to come back
 * to it (`followUpAt`). Nulls clear the park.
 *
 * `task.dueDate` is deliberately NOT touched — a deadline and a "come back to
 * me" are different facts — and neither is `remindAfter`, the reminder
 * workflow's cooldown gate. The key is validated server-side against the
 * org's ENABLED options; the audit trigger records both columns' diffs, so
 * there is no activity row to write.
 *
 * The optional note is recorded as a comment on the case (its conversation
 * lives in `core_comments` on the companion task), which is quiet — no
 * watcher fan-out, same as AR's next step.
 */
export const SetPricelistCaseNextStepInputSchema = z.object({
  caseId: z.uuid(),
  waitingOnKey: z.string().min(1).max(64).nullable(),
  followUpAt: z.string().datetime({ offset: true }).nullable(),
  note: z.string().max(2000).optional(),
});

export const UpdatePricelistCaseInputSchema = z.object({
  caseId: z.uuid(),
  /**
   * Move the case to a different vendor. Absent leaves it alone; there is no
   * null, because `company_id` is NOT NULL — a case is always for someone.
   *
   * Recorded like any other field: `company_id` is in
   * PRICELIST_CASE_AUDITED_FIELDS, so the timeline names the move rather than
   * letting a case change vendor silently.
   */
  companyIds: z.array(z.uuid()).min(1).max(20).optional(),
  /**
   * GEN-3074: the specific vendor#s the case is for. Each must be carried by
   * a company in the case's (new, when `companyIds` is also sent) vendor set
   * — an unknown number is rejected, never used to add a company. Empty
   * clears back to "every vendor# of the companies". Absent leaves them
   * alone, except that numbers whose company left the set are dropped.
   */
  vendorNumbers: z.array(z.string().trim().min(1).max(32)).max(100).optional(),
  /**
   * A patch: absent keys are left alone, an explicit null clears the column.
   * Every change is recorded by the `audit_write_change_log` trigger on
   * `im_pricelist_cases`, which is what makes a moved effective date
   * traceable without any hand-written event kinds.
   */
  details: PricelistCaseDetailsSchema.partial(),
  /** Same patch semantics, for the case-only log columns. */
  logFields: PricelistCaseLogFieldsSchema.partial().optional(),
  notifyEmails: z
    .array(z.string().trim().toLowerCase().email())
    .max(20)
    .optional(),
});

/** How many cases one bulk assignment may touch. */
export const MAX_PRICELIST_CASE_BULK_ASSIGN_IDS = 200;

/**
 * Hand many cases to one person at once — the list's bulk action. Each case
 * is written exactly as `assign` writes one (its companion task's assignee,
 * plus the ASSIGNED watch), so a bulk assignee is notified per case like any
 * other task assignee. Cases that already have the requested owner, whose
 * task is closed or deleted, or that are not the caller's are skipped rather
 * than failing the batch.
 */
export const BulkAssignPricelistCasesInputSchema = z.object({
  caseIds: z.array(z.uuid()).min(1).max(MAX_PRICELIST_CASE_BULK_ASSIGN_IDS),
  assigneeUserId: z.uuid().nullable(),
});

export const BulkAssignPricelistCasesResponseSchema = z.object({
  assigned: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});

/**
 * Record the same next step on many cases at once — the list's bulk action,
 * the counterpart of `setNextStep` for one. The same reason / follow-up pair is
 * written to every case (validated once against the org's ENABLED options),
 * and the optional note lands as a comment on each. Cases whose task is closed
 * or deleted, or that are not the caller's, are skipped rather than failing
 * the batch.
 */
export const BulkSetPricelistCaseNextStepInputSchema = z.object({
  caseIds: z.array(z.uuid()).min(1).max(MAX_PRICELIST_CASE_BULK_ASSIGN_IDS),
  waitingOnKey: z.string().min(1).max(64).nullable(),
  followUpAt: z.string().datetime({ offset: true }).nullable(),
  note: z.string().max(2000).optional(),
});

export const BulkSetPricelistCaseNextStepResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});

export const SetPricelistCaseFilesReadyInputSchema = z.object({
  caseId: z.uuid(),
  filesReadyAt: PricelistDateSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Timeline and comments
// ---------------------------------------------------------------------------

/**
 * One case domain field change, as "Effective date: 2026-10-01 → 2026-11-15".
 *
 * One entry per FIELD rather than per audit row, so a save that moved the
 * effective date and cleared the notes reads as two facts.
 */
export const PricelistCaseFieldChangeEntrySchema = z.object({
  id: z.string(),
  kind: z.literal("case_field_changed"),
  timestamp: z.coerce.date(),
  /** The column, snake_case as the audit trigger keys it. */
  field: z.string(),
  /** JSON as stored in the diff: string, boolean, array or null. */
  previousValue: z.unknown(),
  value: z.unknown(),
  actorUserId: z.uuid().nullable(),
  sourceType: z.string().nullable(),
  sourceDetail: z.string().nullable(),
});

/**
 * A merged timeline entry: either one of the companion task's entries or one
 * of the case's own field diffs.
 *
 * `z.union`, not `z.discriminatedUnion`: `TaskTimelineEntrySchema` is itself a
 * discriminated union, and nesting one inside another's option list is a shape
 * zod does not flatten.
 */
export const PricelistCaseTimelineEntrySchema = z.union([
  TaskTimelineEntrySchema,
  PricelistCaseFieldChangeEntrySchema,
]);

/**
 * A pre-case `im_pricelist_events` row for one of the case's files. Read-only
 * and rendered separately: nothing is ever written to that table for a case.
 */
export const PricelistCaseLegacyEventSchema = PricelistEventSchema.extend({
  documentId: z.uuid(),
});

export const GetPricelistCaseTimelineInputSchema = z.object({
  caseId: z.uuid(),
});

export const GetPricelistCaseTimelineResponseSchema = z.object({
  /** Newest first: the task's timeline merged with the case's field diffs. */
  entries: z.array(PricelistCaseTimelineEntrySchema),
  legacyEvents: z.array(PricelistCaseLegacyEventSchema),
  /** True when more history exists than the fixed cap returned. */
  truncated: z.boolean(),
  vendorNameByCompanyId: z.record(z.string(), z.string()),
});

export const ListPricelistCaseCommentsInputSchema = z.object({
  caseId: z.uuid(),
  limit: z.number().int().min(1).max(200).optional(),
  offset: z.number().int().nonnegative().optional(),
});

export const AddPricelistCaseCommentInputSchema = z.object({
  caseId: z.uuid(),
  body: z.string().min(1).max(50000),
});

export const UpdatePricelistCaseCommentInputSchema = z.object({
  caseId: z.uuid(),
  commentId: z.uuid(),
  body: z.string().min(1).max(50000),
});

export const DeletePricelistCaseCommentInputSchema = z.object({
  caseId: z.uuid(),
  commentId: z.uuid(),
});

export const DeletePricelistCaseCommentResponseSchema = z.object({
  success: z.boolean(),
});

export const pricelistCasesContracts = {
  create: oc
    .input(CreatePricelistCaseInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "POST",
      summary: "Open a pricelist update case",
      description:
        "Opens a vendor price-change case and its companion task in one transaction, with optional title, priority and initial details such as effective date.",
    }),
  list: oc
    .input(ListPricelistCasesInputSchema)
    .output(ListPricelistCasesResponseSchema)
    .route({
      method: "GET",
      summary: "List pricelist update cases",
      description:
        "Paged pricelist update cases, newest first by default. Filter by search, vendor, assignee, waiting-on reason, and effective, received or opened dates.",
    }),
  get: oc
    .input(GetPricelistCaseInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "GET",
      summary: "Get a pricelist update case",
      description:
        "Returns one pricelist update case with its price-change details, companion task, assignee, reminder settings and attached files.",
    }),
  update: oc
    .input(UpdatePricelistCaseInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "PUT",
      summary: "Update a pricelist update case",
      description:
        "Patches a case's vendor, price-change details, log fields and reminder email recipients; every field change is recorded in the case history.",
    }),
  previewFromSelection: oc
    .input(PreviewPricelistCaseSelectionInputSchema)
    .output(PreviewPricelistCaseSelectionResponseSchema)
    .route({
      method: "POST",
      summary: "Preview a case from selected pricelists",
      description:
        "For selected pricelist files, shows their roles, vendors, seeded case details with sources, and any files blocking a new case (for example, already on a case).",
    }),
  createFromSelection: oc
    .input(CreatePricelistCaseFromSelectionInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "POST",
      summary: "Open a case from selected pricelists",
      description:
        "Opens a pricelist update case and its companion task from selected pricelist files, attaching them in the same transaction.",
    }),
  addFiles: oc
    .input(AddFilesToPricelistCaseInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "POST",
      summary: "Add pricelists to an update case",
      description:
        "Attaches selected pricelist files to an existing open pricelist update case.",
    }),
  bulkAssign: oc
    .input(BulkAssignPricelistCasesInputSchema)
    .output(BulkAssignPricelistCasesResponseSchema)
    .route({
      method: "POST",
      summary: "Assign pricelist update cases to a user",
      description:
        "Assigns or unassigns several selected pricelist update cases to one organization user by updating their companion tasks.",
    }),
  assign: oc
    .input(AssignPricelistCaseInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "POST",
      summary: "Assign a pricelist update case",
      description:
        "Assigns a pricelist update case to an organization user, or unassigns it, by updating its companion task.",
    }),
  setNextStep: oc
    .input(SetPricelistCaseNextStepInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "POST",
      summary: "Set a pricelist case's waiting-on reason",
      description:
        "Parks a case under a waiting-on reason with a follow-up date, or clears it; an optional note is added as a comment.",
    }),
  bulkSetNextStep: oc
    .input(BulkSetPricelistCaseNextStepInputSchema)
    .output(BulkSetPricelistCaseNextStepResponseSchema)
    .route({
      method: "POST",
      summary: "Set waiting-on reason on pricelist cases",
      description:
        "Parks several selected pricelist update cases under one waiting-on reason and follow-up date, or clears their waiting-on state.",
    }),
  setFilesReady: oc
    .input(SetPricelistCaseFilesReadyInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "POST",
      summary: "Set a pricelist case's files-ready date",
      description:
        "Sets or clears the date the vendor's files are expected, which drives the upload reminder on a pricelist update case.",
    }),
  detachFile: oc
    .input(DetachPricelistCaseFileInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a file from a pricelist case",
      description:
        "Detaches one file from an open pricelist update case; the pricelist document itself is kept.",
    }),
  updateFile: oc
    .input(UpdatePricelistCaseFileInputSchema)
    .output(PricelistCaseResponseSchema)
    .route({
      method: "POST",
      summary: "Update a pricelist case file",
      description:
        "Updates notes or the deprecated flag on a file attached to a pricelist update case, without detaching it.",
    }),
  timeline: oc
    .input(GetPricelistCaseTimelineInputSchema)
    .output(GetPricelistCaseTimelineResponseSchema)
    .route({
      method: "GET",
      summary: "Get a pricelist case's history",
      description:
        "Returns a pricelist update case's timeline: its companion task's activity merged with the case's own field changes.",
    }),
  listComments: oc
    .input(ListPricelistCaseCommentsInputSchema)
    .output(ListCommentsResponseSchema)
    .route({
      method: "GET",
      summary: "List comments on a pricelist case",
      description:
        "Returns the comments on a pricelist update case's conversation.",
    }),
  addComment: oc
    .input(AddPricelistCaseCommentInputSchema)
    .output(CommentResponseSchema)
    .route({
      method: "POST",
      summary: "Comment on a pricelist update case",
      description:
        "Adds a comment from the current user to a pricelist update case's conversation.",
    }),
  updateComment: oc
    .input(UpdatePricelistCaseCommentInputSchema)
    .output(CommentResponseSchema)
    .route({
      method: "PUT",
      summary: "Edit a comment on a pricelist case",
      description:
        "Changes the text of a comment you wrote on a pricelist update case's conversation.",
    }),
  deleteComment: oc
    .input(DeletePricelistCaseCommentInputSchema)
    .output(DeletePricelistCaseCommentResponseSchema)
    .route({
      method: "DELETE",
      summary: "Delete a comment on a pricelist case",
      description:
        "Deletes a comment you wrote on a pricelist update case's conversation.",
    }),
} as const;

export type PricelistCaseFileRoleDTO = z.infer<
  typeof PricelistCaseFileRoleSchema
>;
export type PricelistCaseTaskDTO = z.infer<typeof PricelistCaseTaskSchema>;
export type PricelistCaseVendor = z.infer<typeof PricelistCaseVendorSchema>;
export type PricelistCaseListRow = z.infer<typeof PricelistCaseListRowSchema>;
export type PricelistCaseDetailsDTO = z.infer<
  typeof PricelistCaseDetailsSchema
>;
export type PricelistCaseFileDTO = z.infer<typeof PricelistCaseFileSchema>;
export type ListPricelistCasesInput = z.infer<
  typeof ListPricelistCasesInputSchema
>;
export type ListPricelistCasesResponse = z.infer<
  typeof ListPricelistCasesResponseSchema
>;
export type GetPricelistCaseInput = z.infer<typeof GetPricelistCaseInputSchema>;
export type PricelistCaseResponse = z.infer<typeof PricelistCaseResponseSchema>;
export type CreatePricelistCaseInput = z.infer<
  typeof CreatePricelistCaseInputSchema
>;
export type UpdatePricelistCaseInput = z.infer<
  typeof UpdatePricelistCaseInputSchema
>;
export type PricelistCaseLogFieldsDTO = z.infer<
  typeof PricelistCaseLogFieldsSchema
>;
export type BulkAssignPricelistCasesInput = z.infer<
  typeof BulkAssignPricelistCasesInputSchema
>;
export type BulkAssignPricelistCasesResponse = z.infer<
  typeof BulkAssignPricelistCasesResponseSchema
>;
export type BulkSetPricelistCaseNextStepInput = z.infer<
  typeof BulkSetPricelistCaseNextStepInputSchema
>;
export type BulkSetPricelistCaseNextStepResponse = z.infer<
  typeof BulkSetPricelistCaseNextStepResponseSchema
>;
export type PricelistCaseSelectionFileDTO = z.infer<
  typeof PricelistCaseSelectionFileSchema
>;
export type PricelistCaseSelectionVendorDTO = z.infer<
  typeof PricelistCaseSelectionVendorSchema
>;
export type PricelistCaseSeedField = z.infer<
  typeof PricelistCaseSeedFieldSchema
>;
export type PricelistCaseSeedSourceDTO = z.infer<
  typeof PricelistCaseSeedSourceSchema
>;
export type PreviewPricelistCaseSelectionInput = z.infer<
  typeof PreviewPricelistCaseSelectionInputSchema
>;
export type PreviewPricelistCaseSelectionResponse = z.infer<
  typeof PreviewPricelistCaseSelectionResponseSchema
>;
export type PricelistCaseFileChoice = z.infer<
  typeof PricelistCaseFileChoiceSchema
>;
export type CreatePricelistCaseFromSelectionInput = z.infer<
  typeof CreatePricelistCaseFromSelectionInputSchema
>;
export type AddFilesToPricelistCaseInput = z.infer<
  typeof AddFilesToPricelistCaseInputSchema
>;
export type DetachPricelistCaseFileInput = z.infer<
  typeof DetachPricelistCaseFileInputSchema
>;
export type UpdatePricelistCaseFileInput = z.infer<
  typeof UpdatePricelistCaseFileInputSchema
>;
export type AssignPricelistCaseInput = z.infer<
  typeof AssignPricelistCaseInputSchema
>;
export type SetPricelistCaseNextStepInput = z.infer<
  typeof SetPricelistCaseNextStepInputSchema
>;
export type SetPricelistCaseFilesReadyInput = z.infer<
  typeof SetPricelistCaseFilesReadyInputSchema
>;
export type PricelistCaseFieldChangeEntryDTO = z.infer<
  typeof PricelistCaseFieldChangeEntrySchema
>;
export type PricelistCaseTimelineEntryDTO = z.infer<
  typeof PricelistCaseTimelineEntrySchema
>;
export type PricelistCaseLegacyEventDTO = z.infer<
  typeof PricelistCaseLegacyEventSchema
>;
export type GetPricelistCaseTimelineResponse = z.infer<
  typeof GetPricelistCaseTimelineResponseSchema
>;
export type ListPricelistCaseCommentsInput = z.infer<
  typeof ListPricelistCaseCommentsInputSchema
>;
export type AddPricelistCaseCommentInput = z.infer<
  typeof AddPricelistCaseCommentInputSchema
>;
export type UpdatePricelistCaseCommentInput = z.infer<
  typeof UpdatePricelistCaseCommentInputSchema
>;
export type DeletePricelistCaseCommentInput = z.infer<
  typeof DeletePricelistCaseCommentInputSchema
>;
